import React from 'react';
import {
  Package,
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  Mail,
  User,
  Plus,
  Check,
  FileText,
  Calendar,
  RotateCcw,
  Cloud,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { fetchLogicAppPayload } from '../services/deliveryService';
import StatusBadge from './ui/StatusBadge';
import ActionedBadge from './ui/ActionedBadge';

export default function DeliveryCard({
  delivery,
  operationType,
  isExpanded,
  onToggle,
  onAddNote,
  onComplete,
  onRetry,
  onSetSoNumber,
  isSelected = false,
  onSelectionToggle,
}) {
  const isReviewOp = operationType === 'Exchange' || operationType === 'Pickup';
  const items = delivery.items || [];
  const resultValue = (operationType === 'SAP_SO' || isReviewOp)
    ? delivery.sapResult
    : delivery.storisResult;
  // Mirrors splitDeliveries in DeliveryContext: only rows in the "Needs Action"
  // section should expose action buttons.
  const isActionable = isReviewOp
    ? !delivery.isActioned && (delivery.sapResult == null || delivery.sapResult === 'Failed')
    : resultValue === 'Failed' && !delivery.isActioned;
  const sourceReference = delivery.sourceReference || '';
  const sapSoNumber = delivery.sapSoNumber || '';

  const [expandedJson, setExpandedJson] = React.useState({});
  const [logicAppData, setLogicAppData] = React.useState(null);
  const [logicAppLoading, setLogicAppLoading] = React.useState(false);
  const [logicAppError, setLogicAppError] = React.useState(null);

  const toggleJson = (key) =>
    setExpandedJson((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleFetchLogicApp = async (e) => {
    e.stopPropagation();
    if (logicAppData) {
      toggleJson('logicApp');
      return;
    }
    setLogicAppLoading(true);
    setLogicAppError(null);
    try {
      const data = await fetchLogicAppPayload(delivery.id);
      setLogicAppData(data);
      setExpandedJson((prev) => ({ ...prev, logicApp: true }));
    } catch (err) {
      setLogicAppError(err.message);
    } finally {
      setLogicAppLoading(false);
    }
  };

  const formatValue = (value) => {
    if (!value) return null;
    const str = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
    // If it looks like JSON, pretty-print it
    if (str.trimStart().startsWith('{') || str.trimStart().startsWith('[')) {
      try {
        return JSON.stringify(JSON.parse(str), null, 2);
      } catch { /* fall through */ }
    }
    // If it looks like XML, indent it
    if (str.trimStart().startsWith('<')) {
      return formatXml(str);
    }
    return str;
  };

  const formatXml = (xml) => {
    let formatted = '';
    let indent = 0;
    const parts = xml.replace(/>\\s*</g, '><').replace(/>\s*</g, '><').split(/(<[^>]+>)/);
    parts.forEach((part) => {
      if (!part.trim()) return;
      if (part.match(/^<\/\w/)) indent--;
      formatted += '  '.repeat(Math.max(indent, 0)) + part.trim() + '\n';
      if (part.match(/^<\w[^>]*[^/]>$/)) indent++;
    });
    return formatted.trimEnd();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    if (timestamp.includes('T')) {
      return timestamp.slice(0, 16).replace('T', ' ');
    }
    return timestamp;
  };

  return (
    <div
      className={`bg-white rounded-xl mb-3 overflow-hidden hover:shadow-md transition-shadow border ${
        isSelected
          ? 'border-locate2-primary ring-1 ring-locate2-primary/30'
          : 'border-locate2-border-light'
      }`}
    >
      <div
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4 flex-1">
          {onSelectionToggle && (
            <input
              type="checkbox"
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelectionToggle(delivery.id)}
              aria-label={`Select order ${delivery.customerReference || delivery.name}`}
              className="w-4 h-4 rounded accent-locate2-primary cursor-pointer"
            />
          )}
          <div className="w-10 h-10 rounded-full bg-locate2-bg-icon flex items-center justify-center">
            <Package size={20} className="text-locate2-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-locate2-dark-blue font-semibold">{delivery.name}</h3>
              {(!isReviewOp || delivery.sapResult) && (
                <StatusBadge result={resultValue} />
              )}
              {isReviewOp && delivery.docType && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-locate2-bg-icon text-locate2-dark-blue">
                  {delivery.docType}
                </span>
              )}
              {delivery.isActioned && <ActionedBadge />}
              {items.length > 1 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-locate2-bg-icon text-locate2-primary">
                  {items.length} items
                </span>
              )}
            </div>
            <p className="text-locate2-light-gray text-sm mt-1">
              Ref: <span className="font-mono text-locate2-primary">{delivery.customerReference || '—'}</span> | Customer: <span className="font-mono">{delivery.customerNo}</span>
              {sourceReference && <> | Source: <span className="font-mono">{sourceReference}</span></>}
              {sapSoNumber && <> | SAP SO: <span className="font-mono text-status-planned">{sapSoNumber}</span></>}
              {delivery.rowKey && (
                <>
                  {' | '}
                  <a
                    href={`https://app.locate2u.com/orders/details/${delivery.rowKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-locate2-primary hover:underline inline-flex items-center gap-1"
                  >
                    View Order
                    <ExternalLink size={12} />
                  </a>
                </>
              )}
            </p>
            {isReviewOp && (delivery.sapSoNumber || delivery.pickupSapSoNumber) && (
              <p className="text-locate2-light-gray text-sm">
                {delivery.sapSoNumber && (
                  <>
                    {operationType === 'Exchange' ? 'Drop SO: ' : 'SO: '}
                    <span className="font-mono text-status-planned">{delivery.sapSoNumber}</span>
                  </>
                )}
                {delivery.pickupSapSoNumber && (
                  <>
                    {delivery.sapSoNumber && ' | '}
                    Pickup SO: <span className="font-mono text-status-planned">{delivery.pickupSapSoNumber}</span>
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isActionable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onComplete(delivery);
              }}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-status-success text-white rounded-lg text-sm font-medium hover:bg-status-success-hover transition-colors"
            >
              <Check size={16} />
              Mark as Actioned
            </button>
          )}
          <div className="text-right hidden sm:block">
            <p className="text-locate2-medium-gray text-sm font-medium">
              {delivery.suburb}, {delivery.state}
            </p>
            <p className="text-locate2-light-gray text-sm">{delivery.reqDate}</p>
          </div>
          {isExpanded ? (
            <ChevronUp size={20} className="text-locate2-light-gray" />
          ) : (
            <ChevronDown size={20} className="text-locate2-light-gray" />
          )}
        </div>
      </div>

      {/* Error summary — visible without expanding for Failed actionable rows */}
      {isActionable && resultValue === 'Failed' && delivery.returnData && (
        <div className="mx-4 mb-3 p-3 bg-status-error-bg border border-status-error/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={14} className="text-status-error" />
            <span className="text-status-error text-xs font-semibold uppercase tracking-wide">
              Error Response
            </span>
          </div>
          <pre className="text-xs text-locate2-medium-gray whitespace-pre-wrap break-words max-h-32 overflow-y-auto font-mono">
            {formatValue(delivery.returnData)}
          </pre>
        </div>
      )}

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-locate2-border-light">
          {/* Doc Type */}
          {delivery.docType && (
            <div className="flex items-center gap-2 pt-4 pb-1">
              <FileText size={16} className="text-locate2-light-gray" />
              <span className="text-locate2-light-gray text-sm font-medium">Doc Type:</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-locate2-bg-icon text-locate2-dark-blue">
                {delivery.docType}
              </span>
            </div>
          )}

          {/* Scheduled Date */}
          {delivery.reqDate && (
            <div className="flex items-center gap-2 pt-4 pb-1">
              <Calendar size={16} className="text-locate2-light-gray" />
              <span className="text-locate2-light-gray text-sm font-medium">Scheduled date:</span>
              <span className="text-locate2-medium-gray text-sm">
                {delivery.reqDate}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            {/* Contact Details */}
            <div className="space-y-3">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide">
                Contact Details
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-locate2-medium-gray text-sm">
                  <User size={16} className="text-locate2-light-gray" />
                  <span>{delivery.name}</span>
                </div>
                <div className="flex items-center gap-2 text-locate2-medium-gray text-sm">
                  <Phone size={16} className="text-locate2-light-gray" />
                  <span>{delivery.mobile}</span>
                </div>
                <div className="flex items-center gap-2 text-locate2-medium-gray text-sm">
                  <Mail size={16} className="text-locate2-light-gray" />
                  <span className="truncate">{delivery.email}</span>
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="space-y-3">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide">
                Delivery Address
              </h4>
              <div className="flex items-start gap-2 text-locate2-medium-gray text-sm">
                <MapPin size={16} className="text-locate2-light-gray mt-0.5 flex-shrink-0" />
                <span>
                  {delivery.unitNo && `${delivery.unitNo}, `}
                  {delivery.streetNo} {delivery.street}
                  <br />
                  {delivery.suburb}, {delivery.state} {delivery.postCode}
                </span>
              </div>
              {delivery.directionNotes && (
                <p className="text-locate2-light-gray text-sm italic">
                  Note: {delivery.directionNotes}
                </p>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          {items.length > 0 && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wider mb-3">
                Order Items ({items.length})
              </h4>
              <div className="overflow-x-auto border border-locate2-border-light rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-locate2-bg-light border-b border-locate2-border-light">
                      <th className="text-left py-2.5 px-4 text-locate2-light-gray font-semibold text-xs uppercase tracking-wider">SKU</th>
                      <th className="text-left py-2.5 px-4 text-locate2-light-gray font-semibold text-xs uppercase tracking-wider">Qty</th>
                      <th className="text-left py-2.5 px-4 text-locate2-light-gray font-semibold text-xs uppercase tracking-wider">Serial No</th>
                      <th className="text-left py-2.5 px-4 text-locate2-light-gray font-semibold text-xs uppercase tracking-wider">Return Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="border-b border-locate2-border-light last:border-b-0 hover:bg-locate2-bg-hover transition-colors">
                        <td className="py-2.5 px-4 text-locate2-medium-gray font-medium font-mono text-xs">{item.sku || '—'}</td>
                        <td className="py-2.5 px-4 text-locate2-medium-gray">{item.qty || '—'}</td>
                        <td className="py-2.5 px-4 text-locate2-medium-gray font-mono text-xs">{item.serial_no || '—'}</td>
                        <td className="py-2.5 px-4 text-locate2-medium-gray">{item.return_reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Delivery Notes */}
          {delivery.deliveryNotes && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light">
              <span className="text-locate2-light-gray font-medium text-sm">Delivery Notes: </span>
              <span className="text-locate2-medium-gray text-sm">{delivery.deliveryNotes}</span>
            </div>
          )}

          {/* Payload & Result JSON */}
          {(delivery.payload || delivery.returnData) && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light space-y-3">
              {delivery.payload && (
                <div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleJson('payload'); }}
                    className="flex items-center gap-2 text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide hover:text-locate2-primary transition-colors"
                  >
                    {expandedJson.payload ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    Payload
                  </button>
                  {expandedJson.payload && (
                    <pre className="mt-2 bg-locate2-bg-light rounded-lg p-3 text-xs text-locate2-medium-gray overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words">
                      {formatValue(delivery.payload)}
                    </pre>
                  )}
                </div>
              )}
              {delivery.returnData && (
                <div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleJson('returnData'); }}
                    className="flex items-center gap-2 text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide hover:text-locate2-primary transition-colors"
                  >
                    {expandedJson.returnData ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    Result
                  </button>
                  {expandedJson.returnData && (
                    <pre className="mt-2 bg-locate2-bg-light rounded-lg p-3 text-xs text-locate2-medium-gray overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words">
                      {formatValue(delivery.returnData)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Logic App Payload */}
          <div className="mt-4 pt-4 border-t border-locate2-border-light">
            <button
              onClick={handleFetchLogicApp}
              disabled={logicAppLoading}
              className="flex items-center gap-2 text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide hover:text-locate2-primary transition-colors disabled:opacity-50"
            >
              {logicAppLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : expandedJson.logicApp ? (
                <ChevronUp size={16} />
              ) : (
                <Cloud size={16} />
              )}
              Logic App Payload
              {logicAppData?.cached && (
                <span className="text-xs font-normal text-locate2-light-gray ml-1">(cached)</span>
              )}
            </button>
            {logicAppError && (
              <p className="mt-2 text-sm text-status-error">{logicAppError}</p>
            )}
            {expandedJson.logicApp && logicAppData && (
              <div>
                {logicAppData.runId && (
                  <p className="mt-2 text-xs text-locate2-light-gray">
                    Run: {logicAppData.runId}
                    {logicAppData.startTime && ` | ${logicAppData.startTime.slice(0, 19).replace('T', ' ')}`}
                  </p>
                )}
                <pre className="mt-2 bg-locate2-bg-light rounded-lg p-3 text-xs text-locate2-medium-gray overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words">
                  {formatValue(logicAppData.payload)}
                </pre>
              </div>
            )}
          </div>

          {/* Action Notes Section */}
          {delivery.actionNotes && delivery.actionNotes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide mb-3">
                Action Notes
              </h4>
              <div className="space-y-2">
                {delivery.actionNotes.map((note, index) => (
                  <div key={index} className="bg-locate2-bg-light rounded-lg p-3">
                    <p className="text-locate2-medium-gray text-sm">{note.text}</p>
                    <p className="text-locate2-light-gray text-xs mt-1">
                      {formatTimestamp(note.timestamp)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-4 pt-4 border-t border-locate2-border-light flex flex-wrap gap-3">
            {/* Always available: Add Note */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddNote(delivery);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent border-2 border-locate2-primary text-locate2-primary rounded-lg font-medium hover:bg-locate2-primary hover:text-white transition-colors"
            >
              <Plus size={18} />
              Add Note
            </button>

            {/* Only for items that need action */}
            {isActionable && (
              <>
                {(operationType === 'SAP_SO' || isReviewOp) && resultValue === 'Failed' && onSetSoNumber && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetSoNumber(delivery);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent border-2 border-status-planned text-status-planned rounded-lg font-medium hover:bg-status-planned hover:text-white transition-colors"
                  >
                    <FileText size={18} />
                    Set SO Number
                  </button>
                )}
                {(operationType === 'SAP_SO' || isReviewOp) && resultValue === 'Failed' && onRetry && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry(delivery);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent border-2 border-status-warning text-status-warning rounded-lg font-medium hover:bg-status-warning hover:text-white transition-colors"
                  >
                    <RotateCcw size={18} />
                    Retry SAP
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete(delivery);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-status-success text-white rounded-lg font-medium hover:bg-status-success-hover transition-colors"
                >
                  <Check size={18} />
                  Mark as Actioned
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
